import React, { useState, useRef, useEffect } from 'react';
import { HiOutlineCalendar } from 'react-icons/hi';
import { IoChevronDown } from 'react-icons/io5';
import './searchableEventSelect.css';

interface EventOption {
  event_details: string;
  event_name: string;
}

interface SearchableEventSelectProps {
  events: EventOption[];
  selectedEventId: string;
  onSelect: (eventId: string) => void;
}

function SearchableEventSelect({
  events,
  selectedEventId,
  onSelect,
}: SearchableEventSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedEvent = events.find((item) => item.event_details === selectedEventId);
  const filteredEvents = events.filter((item) =>
    item.event_name.toLowerCase().includes(searchText.trim().toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (eventId: string) => {
    onSelect(eventId);
    setIsOpen(false);
    setSearchText('');
  };

  const displayValue = isOpen
    ? searchText
    : selectedEvent?.event_name || '';

  return (
    <div className="searchable-select" ref={containerRef}>
      <div
        className={`searchable-select__control${isOpen ? ' searchable-select__control--open' : ''}`}
      >
        <HiOutlineCalendar className="searchable-select__icon" aria-hidden="true" />
        <input
          type="text"
          className="searchable-select__input"
          value={displayValue}
          placeholder="Search events..."
          onChange={(e) => {
            setSearchText(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearchText('');
          }}
        />
        <button
          type="button"
          className="searchable-select__toggle"
          aria-label="Toggle event list"
          onClick={() => {
            setIsOpen((prev) => !prev);
            if (isOpen) setSearchText('');
          }}
        >
          <IoChevronDown />
        </button>
      </div>

      {isOpen && (
        <ul className="searchable-select__dropdown" role="listbox">
          {filteredEvents.length > 0 ? (
            filteredEvents.map((item) => (
              <li
                key={item.event_details}
                role="option"
                aria-selected={item.event_details === selectedEventId}
              >
                <button
                  type="button"
                  className={`searchable-select__option${
                    item.event_details === selectedEventId
                      ? ' searchable-select__option--selected'
                      : ''
                  }`}
                  onClick={() => handleSelect(item.event_details)}
                >
                  {item.event_name}
                </button>
              </li>
            ))
          ) : (
            <li className="searchable-select__empty">No matching events found</li>
          )}
        </ul>
      )}
    </div>
  );
}

export default SearchableEventSelect;
